import { useQueryClient } from '@tanstack/react-query';

import Button from '@/components/Button/Button';
import Modal from '@/components/Modal/Modal';
import {
  type HelpscoutInfo,
  helpscoutQueryKeys,
} from '@/entities/(integration)/helpscout';
import useModalState from '@/hooks/pure/useModalState';
import useFeedbackSourceStore from '@/stores/Feedback/FeedbackSourceStore';
import useWorkspaceStore from '@/stores/WorkspaceStore';
import { SOURCE_TYPE } from '@/types/feedbackConst';

import IntegrationImportModal from '../../components/IntegrationImportModal';
import { ProviderContainer } from '../../components/ProviderContainer';
import type { IntegrationItemProps } from '../../types';
import HelpscoutConnectModal from './HelpscoutConnectModal';
import HelpScoutMailboxModal from './HelpScoutMailboxModal';
import { useHelpScout } from './useHelpScout';

export const HelpScoutItem = ({ isConnector }: IntegrationItemProps) => {
  const currWorkspace = useWorkspaceStore((state) => state.currWorkspace);
  const queryClient = useQueryClient();
  const fetchFeedbackSources = useFeedbackSourceStore(
    (state) => state.fetchFeedbackSources,
  );

  const {
    helpscoutWorkspace,
    deleteMutation,
    importMutation,
    updateMailboxesMutation,
  } = useHelpScout(!isConnector);

  const connectModalState = useModalState();
  const mailboxModalState = useModalState<{ isNew: boolean }>({
    isNew: false,
  });
  const importModalState = useModalState<{
    workspace: HelpscoutInfo;
    isNew: boolean;
  }>();

  const handleConnectComplete = (workspace: HelpscoutInfo | null) => {
    if (!currWorkspace) return;
    if (workspace) {
      queryClient.setQueryData(
        helpscoutQueryKeys.all(currWorkspace.alias),
        workspace,
      );
    }

    fetchFeedbackSources(currWorkspace.alias);
    connectModalState.close();
    mailboxModalState.open({ modalState: { isNew: true } });
  };

  const handleDisconnect = () => {
    deleteMutation.mutate();
    if (currWorkspace) {
      fetchFeedbackSources(currWorkspace.alias);
    }
  };

  const handleImport = (dateRange: NullableDateRangeT) => {
    importMutation.mutate({ dateRange });
    importModalState.close();
  };

  const handleSaveMailboxes = (mailbox_ids: string[]) => {
    updateMailboxesMutation.mutate({ mailbox_ids });
  };

  if (isConnector && helpscoutWorkspace) return null;

  return (
    <>
      <ProviderContainer
        multiInfoEnabled={false}
        connected={!!helpscoutWorkspace}
        source={SOURCE_TYPE.HELPSCOUT}
        description="Import conversations as feedback to Syncly."
        connectedDisplay={'Help Scout'}
        info={helpscoutWorkspace!}
        onDisconnect={handleDisconnect}
        onConnectClick={() => connectModalState.open()}
        additionalActions={(workspace) => (
          <div className="flex gap-2">
            <Button
              variant="outlined"
              onClick={() =>
                importModalState.open({
                  modalState: {
                    workspace,
                    isNew: false,
                  },
                })
              }
            >
              One-time import
            </Button>
            <Button
              variant="outlined"
              onClick={() =>
                mailboxModalState.open({ modalState: { isNew: false } })
              }
            >
              Manage Mailbox
            </Button>
          </div>
        )}
      />
      <Modal
        status={connectModalState.status}
        closeModal={connectModalState.close}
      >
        <HelpscoutConnectModal onConnectComplete={handleConnectComplete} />
      </Modal>
      <Modal
        status={mailboxModalState.status}
        closeModal={mailboxModalState.close}
      >
        {helpscoutWorkspace && currWorkspace && (
          <HelpScoutMailboxModal
            workspaceAlias={currWorkspace.alias}
            mailbox_ids={helpscoutWorkspace.mailbox_ids || []}
            onSave={handleSaveMailboxes}
            isNew={mailboxModalState.modalState.isNew}
          />
        )}
      </Modal>
      <Modal
        status={importModalState.status}
        closeModal={importModalState.close}
        useBackgroundClose
      >
        {helpscoutWorkspace && (
          <IntegrationImportModal
            isNew={importModalState.modalState?.isNew || false}
            title={'Import feedback'}
            subTitle={'HelpScout'}
            subTitleList={helpscoutWorkspace.alias}
            historyList={helpscoutWorkspace.history || []}
            onSubmitImport={(start_date, end_date) =>
              handleImport({ start_date, end_date })
            }
          />
        )}
      </Modal>
    </>
  );
};
